/**
 * PlanComparePage — võrdleb L1 heuristikat ja EMHASS MILP optimum-it
 * sama input-andmestiku põhjal. EI mõjuta tegelikku juhtimist.
 *
 * Kasutus: vali park + kuupäev, vajuta "Arvuta võrdlus" → näeb 96-slot kõrvuti.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParks } from '../contexts/ParksContext';

const SLOTS_PER_DAY = 96;

// Mode → värv (sama mis ControlPage-il)
const MODE_COLORS = {
  charging:    { bg: '#fff3e0', fg: '#e65100', label: 'Laeb' },
  max_sell:    { bg: '#fce4ec', fg: '#ad1457', label: 'Max müük' },
  hold:        { bg: '#e3f2fd', fg: '#1565c0', label: 'Hoia' },
  export_prio: { bg: '#e8f5e9', fg: '#2e7d32', label: 'Eksport' },
  battery_use: { bg: '#f3e5f5', fg: '#6a1b9a', label: 'Aku kasut' },
  stop:        { bg: '#eceff1', fg: '#37474f', label: 'Stop' },
  pause:       { bg: '#eceff1', fg: '#37474f', label: 'Paus' },
};

function fmtTime(idx) {
  const h = Math.floor(idx / 4);
  const m = (idx % 4) * 15;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function ModeCell({ mode }) {
  const m = MODE_COLORS[mode] || { bg: '#f5f5f5', fg: '#666', label: mode || '—' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: m.bg, color: m.fg, fontSize: 11, fontWeight: 500,
      minWidth: 70, textAlign: 'center',
    }}>{m.label}</span>
  );
}

export function PlanComparePage() {
  const { data: parksData } = useParks();
  const onlineParks = (parksData?.parks || []).filter(p => p?.online && p?.name && !p?.disabled);

  const today = new Date().toISOString().slice(0, 10);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [date, setDate] = useState(today);
  const [compareResult, setCompareResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Vaikimisi vali esimene park
  useEffect(() => {
    if (selectedSlot == null && onlineParks.length > 0) {
      setSelectedSlot(onlineParks[0].slot);
    }
  }, [onlineParks, selectedSlot]);

  const fetchCompare = async () => {
    if (selectedSlot == null) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch(`/api/sites/${selectedSlot}/plan-compare?date=${date}&levels=L1,EMHASS`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setCompareResult(d);
    } catch (e) {
      setErr(e.message);
      setCompareResult(null);
    } finally {
      setLoading(false);
    }
  };

  // Statistika
  const stats = useMemo(() => {
    if (!compareResult) return null;
    const countModes = (modes) => {
      const counts = {};
      if (!Array.isArray(modes)) return counts;
      for (const m of modes) counts[m] = (counts[m] || 0) + 1;
      return counts;
    };
    return {
      L1: compareResult.L1 ? { profit: compareResult.L1.expectedProfitEur, modes: countModes(compareResult.L1.modes) } : null,
      EMHASS: compareResult.EMHASS && !compareResult.EMHASS.error
        ? { profit: compareResult.EMHASS.expectedProfitEur, modes: countModes(compareResult.EMHASS.modes) }
        : null,
      emhassError: compareResult.EMHASS?.error || null,
    };
  }, [compareResult]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>
        Plaani võrdlus — L1 heuristika vs EMHASS MILP
      </h1>
      <p style={{ color: '#666', marginBottom: 24, fontSize: 13 }}>
        Võrdleb praegu deploy-tud L1 algoritmi ja EMHASS-i (MILP optimum CVXPY+HiGHS-iga).
        Tulemus ei mõjuta tegelikku juhtimist — see on ainult analüüsiks.
      </p>

      {/* Kontroll-ribad */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Park</label>
          <select
            value={selectedSlot ?? ''}
            onChange={e => setSelectedSlot(Number(e.target.value))}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', minWidth: 200 }}
          >
            {onlineParks.map(p => (
              <option key={p.slot} value={p.slot}>{p.slot}. {p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Kuupäev</label>
          <input
            type="date" value={date}
            onChange={e => setDate(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc' }}
          />
        </div>
        <button
          onClick={fetchCompare}
          disabled={loading || selectedSlot == null}
          style={{
            padding: '8px 16px', borderRadius: 4, border: 'none',
            background: loading ? '#999' : '#11093E', color: '#fff',
            fontWeight: 500, cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? 'Arvutab…' : 'Arvuta võrdlus'}
        </button>
      </div>

      {err && (
        <div style={{ padding: 12, background: '#ffebee', color: '#c62828', borderRadius: 4, marginBottom: 16 }}>
          Viga: {err}
        </div>
      )}

      {compareResult && stats && (
        <>
          {/* Reaalse tulu rida (täna + homme) */}
          {compareResult.actualProfitToday != null && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 16,
              background: '#fff3e0', padding: 12, borderRadius: 6, border: '1px solid #ffcc80',
            }}>
              <div>
                <div style={{ fontSize: 12, color: '#e65100', marginBottom: 2 }}>REAALNE — TÄNA</div>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#bf360c' }}>
                  €{compareResult.actualProfitToday.toFixed(2)}
                </div>
                {compareResult.actualBatterySavingsToday != null && (
                  <div style={{ fontSize: 11, color: '#e65100', marginTop: 2 }}>
                    Aku säästis: <b>€{compareResult.actualBatterySavingsToday.toFixed(2)}</b> (vs ilma akuta)
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#e65100', marginBottom: 2 }}>REAALNE — HOMME</div>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#bf360c' }}>
                  €{(compareResult.actualProfitTomorrow ?? 0).toFixed(2)}
                  <span style={{ fontSize: 11, color: '#999', marginLeft: 8, fontWeight: 400 }}>(jälitatakse päeva jooksul)</span>
                </div>
              </div>
            </div>
          )}

          {/* Tulu-võrdlus */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 6 }}>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>L1 heuristika (ennustus)</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: '#11093E' }}>
                €{stats.L1?.profit?.toFixed(2) ?? '—'}
              </div>
              {compareResult.actualProfitToday != null && stats.L1 && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                  Realiseeritud: <b style={{
                    color: compareResult.actualProfitToday >= stats.L1.profit * 0.8 ? '#1b5e20' : '#c62828',
                  }}>€{compareResult.actualProfitToday.toFixed(2)}</b>
                  {' · '}
                  {compareResult.actualProfitToday !== 0 && (
                    <>{Math.round((compareResult.actualProfitToday / Math.max(0.01, stats.L1.profit)) * 100)}% ennustusest</>
                  )}
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                {stats.L1 && Object.entries(stats.L1.modes).map(([m, n]) => (
                  <span key={m} style={{ marginRight: 12 }}>
                    {MODE_COLORS[m]?.label || m}: <b>{n}</b>
                  </span>
                ))}
              </div>
            </div>
            <div style={{ background: '#e8f5e9', padding: 16, borderRadius: 6 }}>
              <div style={{ fontSize: 13, color: '#2e7d32', marginBottom: 4 }}>EMHASS MILP optimum</div>
              {stats.emhassError ? (
                <div style={{ fontSize: 14, color: '#c62828' }}>Viga: {stats.emhassError}</div>
              ) : (
                <>
                  <div style={{ fontSize: 28, fontWeight: 600, color: '#1b5e20' }}>
                    €{stats.EMHASS?.profit?.toFixed(2) ?? '—'}
                    <span style={{ fontSize: 12, color: '#666', fontWeight: 400, marginLeft: 8 }}>
                      ({compareResult.EMHASS?.horizonHours ?? 24}h kokku)
                    </span>
                  </div>
                  {compareResult.EMHASS?.todayProfitEur != null && (
                    <div style={{ marginTop: 4, fontSize: 13, color: '#2e7d32' }}>
                      <b>Täna ennust:</b> €{compareResult.EMHASS.todayProfitEur.toFixed(2)}
                      {compareResult.actualProfitToday != null && (
                        <> · <b>Reaalne:</b> <span style={{
                          color: compareResult.actualProfitToday >= compareResult.EMHASS.todayProfitEur * 0.8 ? '#1b5e20' : '#c62828',
                        }}>€{compareResult.actualProfitToday.toFixed(2)}</span></>
                      )}
                      {compareResult.EMHASS.tomorrowProfitEur != null && compareResult.EMHASS.tomorrowProfitEur !== 0 && (
                        <><br /><b>Homme ennust:</b> €{compareResult.EMHASS.tomorrowProfitEur.toFixed(2)}
                          {compareResult.actualProfitTomorrow != null && compareResult.actualProfitTomorrow !== 0 && (
                            <> · <b>Reaalne:</b> €{compareResult.actualProfitTomorrow.toFixed(2)}</>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 12, color: '#2e7d32' }}>
                    {stats.EMHASS && Object.entries(stats.EMHASS.modes).map(([m, n]) => (
                      <span key={m} style={{ marginRight: 12 }}>
                        {MODE_COLORS[m]?.label || m}: <b>{n}</b>
                      </span>
                    ))}
                  </div>
                  {stats.L1 && stats.EMHASS && (
                    <div style={{ marginTop: 8, fontSize: 12, fontWeight: 500 }}>
                      Täna vahe L1-st: <b style={{ color: (compareResult.EMHASS?.todayProfitEur ?? stats.EMHASS.profit) > stats.L1.profit ? '#1b5e20' : '#c62828' }}>
                        {(compareResult.EMHASS?.todayProfitEur ?? stats.EMHASS.profit) > stats.L1.profit ? '+' : ''}
                        €{((compareResult.EMHASS?.todayProfitEur ?? stats.EMHASS.profit) - stats.L1.profit).toFixed(2)}
                      </b>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Slot-kaupa võrdlus */}
          <div style={{ background: '#fff', borderRadius: 6, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', background: '#fafafa' }}>
              <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Slot-kaupa võrdlus (96 × 15 min)</h2>
            </div>
            <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #ccc' }}>Aeg</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Solar (kWh)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Tarbi (kWh)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Buy</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Sell</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #ccc', background: '#e3f2fd' }}>L1 režiim</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #ccc', background: '#e3f2fd' }}>L1 SoC%</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #ccc', background: '#e8f5e9' }}>EMHASS režiim</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #ccc', background: '#e8f5e9' }}>EMHASS SoC%</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #ccc', background: '#e8f5e9' }} title="EMHASS-i ennustus selle slot-i PCS-i keskmiseks võimsuseks — MITTE käsk PCS-le. Tegelik juhtimine teeb PlanController GRID-meetri feedback-iga.">EMHASS PCS kW <span style={{ fontSize: 10, color: '#999' }}>(plan)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {compareResult.slots.map((s, i) => {
                    const isCurrentOrPast = compareResult.slotStart >= 0 && i <= compareResult.slotStart;
                    const rowBg = isCurrentOrPast ? '#fafafa' : '#fff';
                    return (
                      <tr key={i} style={{ background: rowBg, opacity: isCurrentOrPast ? 0.5 : 1 }}>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace' }}>
                          {fmtTime(s.idx)}
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                          {s.solar_kwh?.toFixed(2)}
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                          {s.cons_kwh?.toFixed(2)}
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                          {s.buy?.toFixed(3)}
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                          {s.sell?.toFixed(3)}
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', background: '#f5fafe' }}>
                          {compareResult.L1?.modes?.[i] ? <ModeCell mode={compareResult.L1.modes[i]} /> : '—'}
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', background: '#f5fafe', fontFamily: 'monospace' }}>
                          {compareResult.L1?.socTraj?.[i]?.toFixed(0)}%
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', background: '#f5fbf5' }}>
                          {compareResult.EMHASS?.modes?.[i] ? <ModeCell mode={compareResult.EMHASS.modes[i]} /> : '—'}
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', background: '#f5fbf5', fontFamily: 'monospace' }}>
                          {compareResult.EMHASS?.socTraj?.[i]?.toFixed(0)}%
                        </td>
                        <td style={{ padding: '4px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', background: '#f5fbf5', fontFamily: 'monospace' }}>
                          {compareResult.EMHASS?.pcsTraj?.[i] != null ? compareResult.EMHASS.pcsTraj[i].toFixed(1) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <p style={{ marginTop: 16, fontSize: 12, color: '#888' }}>
            <b>Mis on mis:</b> L1 = praegu deploy-tud heuristika (sellP90/buyP10 lävi).
            EMHASS = MILP optimum CVXPY+HiGHS solver-iga, jookseb eraldi Docker konteineris (ems-emhass:5000).
            Mõlemad kasutavad sama input-andmestikku: Elering hinnad, Open-Meteo solar prognoos, 7-päeva tarbimise keskmine.
            Hallid read = mineviku slot-id (need on juba kinni).
          </p>
          <p style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
            <b>Tähtis:</b> EMHASS PCS kW on AINULT plaan (15-min keskmine). Tegelik juhtimine teeb PlanController
            1 sek tikuga GRID-meetri otsesest fast-pollist — võimsust reguleeritakse alati reaalajas grid_kw põhjal,
            sõltumata kas plaan tuli L1-st või EMHASS-ist. EMHASS määrab AINULT slot-i režiimi
            (charging/max_sell/hold/export_prio/battery_use). Peakaitse-piirid ja sensor fail-safe alati kehtivad.
          </p>
          {compareResult.EMHASS?.horizonHours && (
            <p style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              EMHASS optimum horizon: <b>{compareResult.EMHASS.horizonHours}h</b>
              {compareResult.EMHASS.horizonHours > 24 && ' (täna + homne Elering hinnad)'}
              {compareResult.EMHASS.horizonHours === 24 && ' (homse hinnad veel saadaval; saab ~14:00)'}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default PlanComparePage;
